import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type {
  AuthIdentityView,
  AuthLoginMethodsView,
  AuthResultView,
  ExternalAuthFlowView,
  LegalAcceptanceInput,
  MarketingAttribution,
  UserView,
} from 'shared';
import { authApi, type UpdateProfileInput } from '@/api/auth.api';
import { getDesktopBridge } from '@/desktop';
import { getMobileClient } from '@/services/mobile-client';
import {
  getAccessToken,
  onAccessTokenChange,
  refreshAccessToken,
  setAccessToken,
  toAppError,
} from '@/api/client';
import { refreshCollaborationAuth } from '@/composables/useCollaborationSocket';
import type { ResourceChangedEvent } from '@/composables/useCollaborationSocket';
import { registerResourceStore } from '@/stores/realtimeDispatcher';

const REFRESH_AHEAD_MS = 60_000;

function accessTokenExpiresAt(token: string): number | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: unknown;
    };
    return typeof decoded.exp === 'number' ? decoded.exp * 1_000 : null;
  } catch {
    return null;
  }
}

export const useAuthStore = defineStore(
  'auth',
  () => {
    const user = ref<UserView | null>(null);
    const initialized = ref(false);
    const initializationError = ref('');
    const availableLoginMethods = ref<AuthLoginMethodsView | null>(null);
    const isAuthenticated = computed(() => Boolean(user.value));
    let refreshTimer: ReturnType<typeof setTimeout> | null = null;

    function clearRefreshTimer(): void {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = null;
    }

    function scheduleTokenRefresh(token = getAccessToken()): void {
      clearRefreshTimer();
      if (!token) return;
      const expiresAt = accessTokenExpiresAt(token);
      if (!expiresAt) return;
      const delay = Math.max(0, expiresAt - Date.now() - REFRESH_AHEAD_MS);
      refreshTimer = setTimeout(() => {
        void renewSession().catch(() => undefined);
      }, delay);
    }

    async function renewSession(): Promise<void> {
      let token: string | null;
      try { token = await refreshAccessToken(); }
      catch (cause) {
        clearRefreshTimer();
        refreshTimer = setTimeout(() => { void renewSession().catch(() => undefined); }, 30_000);
        throw cause;
      }
      if (!token) {
        user.value = null;
        clearRefreshTimer();
      }
    }

    function applySession(session: AuthResultView): void {
      user.value = session.user;
      setAccessToken(session.accessToken);
    }

    onAccessTokenChange((token) => {
      if (!token) {
        user.value = null;
        clearRefreshTimer();
      } else {
        scheduleTokenRefresh(token);
      }
      refreshCollaborationAuth();
    });

    async function login(input: {
      email: string;
      password: string;
      captchaPayload?: string;
    }): Promise<void> {
      applySession(await authApi.login(input));
    }
    async function loginWithPhone(input: {
      challengeId: string;
      code: string;
    }): Promise<ExternalAuthFlowView | null> {
      const result = await authApi.loginWithPhone(input);
      if ('accessToken' in result) {
        applySession(result);
        return null;
      }
      return result;
    }
    async function registerPendingIdentity(input: {
      pendingToken: string;
      displayName: string;
      referralCode?: string;
      legalAcceptance: LegalAcceptanceInput;
    }): Promise<void> {
      applySession(await authApi.registerPendingIdentity(input));
    }
    async function bindPendingIdentity(input: {
      pendingToken: string;
      email: string;
      password: string;
      captchaPayload?: string;
    }): Promise<void> {
      applySession(await authApi.bindPendingIdentity(input));
    }
    async function loginMethods(): Promise<AuthLoginMethodsView> {
      const methods = await authApi.loginMethods();
      availableLoginMethods.value = methods;
      return methods;
    }
    function listLoginMethods(): Promise<AuthIdentityView[]> {
      return authApi.listLoginMethods();
    }
    async function bindEmailPassword(input: {
      email: string;
      password: string;
      challengeId: string;
      code: string;
    }): Promise<UserView> {
      const updated = await authApi.bindEmailPassword(input);
      user.value = updated;
      return updated;
    }
    function requestEmailVerification(input?: { email?: string }) {
      return authApi.requestEmailVerification(input);
    }
    async function confirmEmailVerification(input: {
      challengeId: string;
      code: string;
    }): Promise<UserView> {
      const updated = await authApi.confirmEmailVerification(input);
      user.value = updated;
      return updated;
    }
    function requestPhoneBindChallenge(input: { phone: string; captchaPayload: string }) {
      return authApi.requestPhoneBindChallenge(input);
    }
    function bindPhone(input: { challengeId: string; code: string }): Promise<void> {
      return authApi.bindPhone(input);
    }
    function beginWechatWebBind() {
      return authApi.beginWechatWebBind();
    }
    function confirmWechatWebBind(id: string, pollToken: string) {
      return authApi.confirmWechatWebBind(id, pollToken);
    }
    async function removeLoginMethod(identityId: string): Promise<void> {
      await authApi.removeLoginMethod(identityId);
      await endSession();
    }
    async function register(input: {
      email: string;
      password: string;
      displayName: string;
      referralCode?: string;
      marketing?: MarketingAttribution;
      captchaPayload: string;
      emailVerificationChallengeId?: string;
      emailVerificationCode?: string;
      legalAcceptance: LegalAcceptanceInput;
    }): Promise<void> {
      applySession(await authApi.register(input));
    }

    /** 原生容器完成安全令牌刷新后，复用同一账户状态真源。 */
    function restoreSession(session: AuthResultView | null): void {
      if (session) applySession(session);
      else {
        user.value = null;
        setAccessToken(null);
      }
      initialized.value = true;
    }

    async function initialize(): Promise<void> {
      if (initialized.value) return;
      initializationError.value = '';
      try {
        // 每次原生冷启动都恢复主进程会话，同时注册设备并连接本地运行时。
        if (getDesktopBridge() || getMobileClient()) {
          await renewSession();
          if (!getAccessToken()) return;
        }
        user.value = await authApi.me();
        scheduleTokenRefresh();
      } catch (cause) {
        const error = toAppError(cause);
        if (error.code !== 'SESSION_CHANGED') {
          if (error.status === 401 || error.status === 403) {
            setAccessToken(null);
            user.value = null;
          } else initializationError.value = error.message || '暂时无法恢复登录，请重试';
        }
      } finally {
        initialized.value = !initializationError.value;
      }
    }

    async function logout(): Promise<void> {
      try {
        await authApi.logout();
      } catch {
        /* 本地会话仍须清理 */
      }
      await endSession();
    }

    async function endSession(): Promise<void> {
      try {
        await getMobileClient()?.clearSession();
      } finally {
        user.value = null;
        setAccessToken(null);
      }
    }

    async function updateProfile(input: UpdateProfileInput): Promise<UserView> {
      const updated = await authApi.updateMe(input);
      user.value = updated;
      return updated;
    }
    async function updateCreativeCenter(enabled: boolean): Promise<UserView> {
      const updated = await authApi.updateCreativeCenter(enabled);
      user.value = updated;
      return updated;
    }
    async function uploadAvatar(image: File): Promise<UserView> {
      const updated = await authApi.uploadAvatar(image);
      user.value = updated;
      return updated;
    }

    async function logoutAll(): Promise<void> {
      try {
        await authApi.logoutAll();
      } finally {
        await endSession();
      }
    }

    async function changePassword(input: {
      currentPassword: string;
      newPassword: string;
    }): Promise<void> {
      await authApi.changePassword(input);
      await endSession();
    }

    async function handleAppearanceRealtimeEvent(event: ResourceChangedEvent): Promise<void> {
      if (event.payload.resourceId !== user.value?.id || event.payload.operation === 'deleted')
        return;
      const data = event.payload.data as
        | Pick<UserView, 'appearance' | 'theme' | 'resolvedTheme'>
        | undefined;
      if (data?.appearance)
        user.value = {
          ...user.value,
          appearance: data.appearance,
          ...(data.theme ? { theme: data.theme } : {}),
          ...(data.resolvedTheme ? { resolvedTheme: data.resolvedTheme } : {}),
        };
    }

    registerResourceStore('account-appearance', {
      handleRealtimeEvent: handleAppearanceRealtimeEvent,
    });
    registerResourceStore('account-onboarding', {
      async handleRealtimeEvent(event: ResourceChangedEvent): Promise<void> {
        if (event.payload.resourceId !== user.value?.id) return;
        const data = event.payload.data as Pick<UserView, 'workspaceOnboarding'> | undefined;
        if (data?.workspaceOnboarding) user.value = { ...user.value, workspaceOnboarding: data.workspaceOnboarding };
      },
    });

    return {
      user,
      initialized,
      initializationError,
      isAuthenticated,
      login,
      loginWithPhone,
      registerPendingIdentity,
      availableLoginMethods,
      bindPendingIdentity,
      loginMethods,
      listLoginMethods,
      bindEmailPassword,
      requestEmailVerification,
      confirmEmailVerification,
      requestPhoneBindChallenge,
      bindPhone,
      beginWechatWebBind,
      confirmWechatWebBind,
      removeLoginMethod,
      register,
      restoreSession,
      initialize,
      logout,
      updateProfile,
      updateCreativeCenter,
      uploadAvatar,
      logoutAll,
      changePassword,
    };
  },
  { persist: false },
);
